import { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  Button,
  Space,
  Select,
  Input,
  Segmented,
  Card,
  Tooltip,
  Dropdown,
  App,
} from 'antd'
import { PlusOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import type { AdvancedQueryPayload, SavedFilterScheme } from './savedFilterSchemes'
import { LIST_FIELD_OPTIONS } from './listFields'

const FIELD_OPTIONS = LIST_FIELD_OPTIONS

const OPERATOR_OPTIONS = [
  { value: 'eq', label: '等于' },
  { value: 'ne', label: '不等于' },
  { value: 'contains', label: '包含' },
  { value: 'gt', label: '大于' },
  { value: 'gte', label: '大于等于' },
  { value: 'lt', label: '小于' },
  { value: 'lte', label: '小于等于' },
]

function uid() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

export function emptyAdvancedQuery(): AdvancedQueryPayload {
  return {
    groupLogic: 'AND',
    groups: [
      {
        id: uid(),
        innerLogic: 'AND',
        rows: [{ id: uid(), field: undefined, operator: undefined, value: '' }],
      },
    ],
  }
}

function cloneQuery(q: AdvancedQueryPayload): AdvancedQueryPayload {
  return JSON.parse(JSON.stringify(q)) as AdvancedQueryPayload
}

type Props = {
  open: boolean
  onClose: () => void
  /** 打开时作为草稿初始值 */
  initialQuery: AdvancedQueryPayload
  /** 应用：提交当前草稿并关闭 */
  onQuery: (query: AdvancedQueryPayload) => void
  schemes: SavedFilterScheme[]
  onSaveScheme: (name: string, query: AdvancedQueryPayload) => void | Promise<void>
  onLoadScheme: (scheme: SavedFilterScheme) => void
}

export default function AdvancedQueryModal({
  open,
  onClose,
  initialQuery,
  onQuery,
  schemes,
  onSaveScheme,
  onLoadScheme,
}: Props) {
  const { message } = App.useApp()
  const [draft, setDraft] = useState<AdvancedQueryPayload>(() => cloneQuery(initialQuery))
  const [saveOpen, setSaveOpen] = useState(false)
  const [schemeName, setSchemeName] = useState('')

  useEffect(() => {
    if (open) setDraft(cloneQuery(initialQuery))
  }, [open, initialQuery])

  const schemeMenuItems = useMemo(
    () =>
      schemes.map((s) => ({
        key: s.id,
        label: s.name,
        onClick: () => {
          onLoadScheme(s)
          setDraft(cloneQuery(s.advancedQuery))
        },
      })),
    [schemes, onLoadScheme]
  )

  const setGroupLogic = (v: 'AND' | 'OR') => setDraft((d) => ({ ...d, groupLogic: v }))

  const setInnerLogic = (gid: string, v: 'AND' | 'OR') =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.map((g) => (g.id === gid ? { ...g, innerLogic: v } : g)),
    }))

  const addGroup = () =>
    setDraft((d) => ({
      ...d,
      groups: [
        ...d.groups,
        { id: uid(), innerLogic: 'AND', rows: [{ id: uid(), field: undefined, operator: undefined, value: '' }] },
      ],
    }))

  const removeGroup = (gid: string) =>
    setDraft((d) => {
      if (d.groups.length <= 1) {
        return {
          ...d,
          groups: [{ id: uid(), innerLogic: 'AND', rows: [{ id: uid(), field: undefined, operator: undefined, value: '' }] }],
        }
      }
      return { ...d, groups: d.groups.filter((g) => g.id !== gid) }
    })

  const addRow = (gid: string) =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.map((g) =>
        g.id === gid ? { ...g, rows: [...g.rows, { id: uid(), field: undefined, operator: undefined, value: '' }] } : g
      ),
    }))

  const removeRow = (gid: string, rid: string) =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.map((g) => {
        if (g.id !== gid) return g
        if (g.rows.length <= 1) return { ...g, rows: [{ id: uid(), field: undefined, operator: undefined, value: '' }] }
        return { ...g, rows: g.rows.filter((r) => r.id !== rid) }
      }),
    }))

  const patchRow = (gid: string, rid: string, patch: Partial<{ field?: string; operator?: string; value?: string }>) =>
    setDraft((d) => ({
      ...d,
      groups: d.groups.map((g) =>
        g.id !== gid
          ? g
          : {
              ...g,
              rows: g.rows.map((r) => (r.id === rid ? { ...r, ...patch } : r)),
            }
      ),
    }))

  const handleReset = () => {
    setDraft(emptyAdvancedQuery())
    message.success('已重置条件')
  }

  const handleQuery = () => {
    onQuery(cloneQuery(draft))
    onClose()
    message.success('已应用高级查询')
  }

  const handleSaveSchemeSubmit = () => {
    const name = schemeName.trim()
    if (!name) {
      message.warning('请输入方案名称')
      return
    }
    void Promise.resolve(onSaveScheme(name, cloneQuery(draft))).finally(() => {
      setSaveOpen(false)
      setSchemeName('')
    })
  }

  return (
    <Modal
      title="高级查询"
      open={open}
      onCancel={onClose}
      width={880}
      destroyOnClose
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
          <Space wrap>
            <Button
              onClick={() => {
                setSchemeName('')
                setSaveOpen(true)
              }}
            >
              保存方案
            </Button>
            <Dropdown menu={{ items: schemeMenuItems }} trigger={['click']} disabled={!schemes.length}>
              <Button>方案选择</Button>
            </Dropdown>
          </Space>
          <Space>
            <Button onClick={handleReset}>重置</Button>
            <Button type="primary" onClick={handleQuery}>
              查询
            </Button>
          </Space>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip title="用于设置不同条件组间的条件逻辑">
            <span className="text-gray-600 text-sm">条件组关系：</span>
          </Tooltip>
          <Segmented
            options={[
              { label: '且', value: 'AND' },
              { label: '或', value: 'OR' },
            ]}
            value={draft.groupLogic}
            onChange={(v) => setGroupLogic(v as 'AND' | 'OR')}
          />
        </div>

        <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
          {draft.groups.map((g) => (
            <Card
              key={g.id}
              size="small"
              className="border-blue-100 bg-blue-50/40 dark:bg-blue-950/20 dark:border-blue-900"
              title={<span className="text-sm font-medium">条件组</span>}
              extra={
                <Button type="text" size="small" danger icon={<CloseOutlined />} onClick={() => removeGroup(g.id)}>
                  删除组
                </Button>
              }
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm text-gray-600">以下条件全部执行：</span>
                <Tooltip title="用于设置同个条件组间的条件逻辑">
                  <Segmented
                    size="small"
                    options={[
                      { label: '且', value: 'AND' },
                      { label: '或', value: 'OR' },
                    ]}
                    value={g.innerLogic}
                    onChange={(v) => setInnerLogic(g.id, v as 'AND' | 'OR')}
                  />
                </Tooltip>
              </div>
              <div className="space-y-2">
                {g.rows.map((row) => (
                  <div key={row.id} className="flex flex-wrap items-end gap-2">
                    <Select
                      placeholder="请选择"
                      allowClear
                      style={{ minWidth: 140, flex: '1 1 140px' }}
                      options={FIELD_OPTIONS}
                      value={row.field}
                      onChange={(v) => patchRow(g.id, row.id, { field: v })}
                    />
                    <Select
                      placeholder="运算符"
                      allowClear
                      style={{ minWidth: 120, width: 120 }}
                      options={OPERATOR_OPTIONS}
                      value={row.operator}
                      onChange={(v) => patchRow(g.id, row.id, { operator: v })}
                    />
                    <Input
                      placeholder="请输入"
                      allowClear
                      style={{ minWidth: 160, flex: '2 1 200px' }}
                      value={row.value}
                      onChange={(e) => patchRow(g.id, row.id, { value: e.target.value })}
                    />
                    <Tooltip title="删除条件行">
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeRow(g.id, row.id)} />
                    </Tooltip>
                  </div>
                ))}
                <Button type="link" icon={<PlusOutlined />} onClick={() => addRow(g.id)} className="px-0">
                  添加条件
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <Button type="link" icon={<PlusOutlined />} onClick={addGroup} className="px-0">
          添加条件组
        </Button>
      </div>

      <Modal
        title="保存方案"
        open={saveOpen}
        onOk={handleSaveSchemeSubmit}
        onCancel={() => {
          setSaveOpen(false)
          setSchemeName('')
        }}
        okText="保存"
        destroyOnClose
      >
        <Input
          placeholder="请输入方案名称"
          maxLength={40}
          allowClear
          value={schemeName}
          onChange={(e) => setSchemeName(e.target.value)}
          onPressEnter={handleSaveSchemeSubmit}
        />
      </Modal>
    </Modal>
  )
}
